import { useEffect } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

export const Section = ({ id, className, children, ...rest }) => (
  <section id={id} className={cn("py-20 sm:py-24 lg:py-32", className)} {...rest}>
    <div className="lc-container">{children}</div>
  </section>
);

export const Reveal = ({ children, delay = 0, className }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const SectionHeading = ({ eyebrow, title, intro, className, center = false }) => (
  <Reveal className={cn("max-w-2xl", center && "mx-auto text-center", className)}>
    {eyebrow && (
      <p className={cn("lc-eyebrow", center && "justify-center")}>
        <span className="h-px w-6 bg-[#D4A437]/70" aria-hidden="true" />
        {eyebrow}
      </p>
    )}
    <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#F7F5F0] leading-[1.05]">
      {title}
    </h2>
    {intro && <p className="mt-5 text-base md:text-lg text-[#A1A1AA] leading-relaxed">{intro}</p>}
  </Reveal>
);

const SITE = process.env.REACT_APP_CANONICAL_URL;
const DEFAULT_TITLE = "WeeLogRun | Monthly Firewood Subscription & Log Delivery in Newtownabbey";

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export const breadcrumb = (name, path) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name, item: `${SITE}${path}` },
  ],
});

export const Seo = ({ title, description, path, noindex = false, jsonLd }) => {
  const ld = JSON.stringify(jsonLd || null);
  useEffect(() => {
    const fullTitle = title ? `${title} | WeeLogRun` : DEFAULT_TITLE;
    document.title = fullTitle;
    if (description) {
      setMeta("name", "description", description);
      setMeta("property", "og:description", description);
      setMeta("name", "twitter:description", description);
    }
    setMeta("property", "og:title", fullTitle);
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
    if (noindex) {
      document.head.querySelector('link[rel="canonical"]')?.remove();
      setMeta("property", "og:url", `${SITE}${window.location.pathname}`);
    } else if (path) {
      setCanonical(`${SITE}${path}`);
      setMeta("property", "og:url", `${SITE}${path}`);
    }
    document.getElementById("page-jsonld")?.remove();
    if (ld !== "null") {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = "page-jsonld";
      s.text = ld;
      document.head.appendChild(s);
    }
  }, [title, description, path, noindex, ld]);
  return null;
};

export const PageHero = ({ eyebrow, title, intro, image, children }) => (
  <section className="relative overflow-hidden border-b border-[#1f1f1f] lc-grain">
    {image && (
      <>
        <img src={image} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-[#080808]/70" aria-hidden="true" />
      </>
    )}
    <div className="relative lc-container pt-32 pb-16 sm:pt-40 sm:pb-20">
      <Reveal className="max-w-3xl">
        <p className="lc-eyebrow">
          <span className="h-px w-6 bg-[#D4A437]/70" aria-hidden="true" />
          {eyebrow}
        </p>
        <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02]">
          {title}
        </h1>
        {intro && <p className="mt-6 text-base md:text-lg text-[#A1A1AA] leading-relaxed max-w-2xl">{intro}</p>}
        {children}
      </Reveal>
    </div>
  </section>
);

export const Placeholderize = ({ text }) =>
  text.split(/(\[[^\]]+\])/g).map((part, i) =>
    part.startsWith("[") ? (
      <span key={i} className="lc-placeholder">
        {part}
      </span>
    ) : (
      part
    ),
  );
